import dayjs from '~/timeInit';

import { formatDateISOUTC, formatDateSlashes } from '~/services/TimeService';

import { daysOfWeek } from '~/utils/daysOfWeek';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { blackoutAppliesToRoomType, classifyDaysScope, classifyRoomsScope } from './blackoutScopeClassifier';
import type { SalesforceBlackoutDate } from './types';

export interface RoomRateBlackoutAssignment {
  href?: string;
  linkedSalesforceBlackoutIds: Array<string>;
  ratePlanCode?: string;
  ratePlanName: string;
  roomBlackoutDate: App.BlackoutDate;
  roomName: string;
  roomRateId: string;
  roomTypeId: string;
}

export interface RoomRateBlackoutGroup {
  blackoutDates: Array<App.BlackoutDate>;
  href?: string;
  ratePlanCode?: string;
  ratePlanName: string;
  roomRateId: string;
}

export interface RoomBlackoutGroup {
  rateGroups: Array<RoomRateBlackoutGroup>;
  roomName: string;
  roomTypeId: string;
}

export function hasValidBlackoutRange(from: string, to: string): boolean {
  return dayjs.utc(from).isValid() && dayjs.utc(to).isValid();
}

export function formatBlackoutRange(from: string, to: string): string {
  if (!hasValidBlackoutRange(from, to)) {
    return 'Not set in Salesforce';
  }

  return `${formatDateSlashes(from)} - ${formatDateSlashes(to)}`;
}

function isMatchingSalesforceBlackout(
  blackoutDate: SalesforceBlackoutDate,
  roomBlackoutDate: App.BlackoutDate,
  roomTypeName: string,
  roomTypeNames: Array<string>,
) {
  const sameDateRange =
    formatDateISOUTC(blackoutDate.from__c) === formatDateISOUTC(roomBlackoutDate.start_date) &&
    formatDateISOUTC(blackoutDate.to__c) === formatDateISOUTC(roomBlackoutDate.end_date);

  if (!sameDateRange) {
    return false;
  }

  const days = classifyDaysScope(blackoutDate.name);

  if (days.source === 'explicit') {
    const explicitDayKeys = new Set(days.matchedDays.map((day) => day.toLowerCase()));
    const sameDays = daysOfWeek.every((day) => Boolean(roomBlackoutDate[day.key]) === explicitDayKeys.has(day.key));

    if (!sameDays) {
      return false;
    }
  }

  const rooms = classifyRoomsScope(blackoutDate.name, roomTypeNames);

  if (rooms.source === 'needs_review' || rooms.scope === 'all') {
    return true;
  }

  const isMatchedRoom = rooms.matchedRooms.includes(roomTypeName);
  return rooms.scope === 'except' ? !isMatchedRoom : isMatchedRoom;
}

export function buildRoomRateBlackoutAssignments({
  blackoutDates,
  blackoutDatesByRoomRate,
  propertyId,
  roomRateSelections,
  roomTypeNames,
  vendorId,
}: {
  blackoutDates: Array<SalesforceBlackoutDate>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  propertyId?: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypeNames: Array<string>;
  vendorId?: string;
}): Array<RoomRateBlackoutAssignment> {
  return roomRateSelections.flatMap(({ roomType, roomRate }) =>
    (blackoutDatesByRoomRate[roomRate.id] ?? []).map((roomBlackoutDate) => ({
      href:
        vendorId && propertyId
          ? `/vendors/${vendorId}/properties/${propertyId}/room-types/${roomType.id}/room-rates/${roomRate.id}`
          : undefined,
      linkedSalesforceBlackoutIds: blackoutDates
        .filter((blackoutDate) =>
          isMatchingSalesforceBlackout(blackoutDate, roomBlackoutDate, roomType.name, roomTypeNames),
        )
        .map((blackoutDate) => blackoutDate.sfid),
      ratePlanCode: roomRate.rate_plan?.rate_plan_code,
      ratePlanName: roomRate.rate_plan?.name ?? 'Unknown rate plan',
      roomBlackoutDate,
      roomName: roomType.name,
      roomRateId: roomRate.id,
      roomTypeId: roomType.id,
    })),
  );
}

export function buildAppliedRoomsByBlackoutId(
  assignments: Array<RoomRateBlackoutAssignment>,
): Record<string, Array<RoomRateBlackoutAssignment>> {
  const assignmentsByBlackoutId: Record<string, Array<RoomRateBlackoutAssignment>> = {};

  assignments.forEach((assignment) => {
    assignment.linkedSalesforceBlackoutIds.forEach((blackoutId) => {
      assignmentsByBlackoutId[blackoutId] = [...(assignmentsByBlackoutId[blackoutId] ?? []), assignment];
    });
  });

  return assignmentsByBlackoutId;
}

export function getUnmatchedRoomRateBlackoutAssignments(
  assignments: Array<RoomRateBlackoutAssignment>,
): Array<RoomRateBlackoutAssignment> {
  return assignments.filter((assignment) => assignment.linkedSalesforceBlackoutIds.length === 0);
}

export type BlackoutAvailability = 'live' | 'upcoming' | 'expired' | 'inactive-today' | 'invalid';

export function getBlackoutAvailability(roomBlackoutDate: App.BlackoutDate, now = dayjs.tz()): BlackoutAvailability {
  const startDate = dayjs.utc(roomBlackoutDate.start_date);
  const endDate = dayjs.utc(roomBlackoutDate.end_date);

  if (!startDate.isValid() || !endDate.isValid()) {
    return 'invalid';
  }

  const today = now.format('YYYY-MM-DD');
  const start = startDate.format('YYYY-MM-DD');
  const end = endDate.format('YYYY-MM-DD');

  if (today < start) {
    return 'upcoming';
  }

  if (today > end) {
    return 'expired';
  }

  const currentDay = daysOfWeek.find((day) => day.fullName === now.format('dddd'));
  return currentDay && roomBlackoutDate[currentDay.key] ? 'live' : 'inactive-today';
}

export interface MissingBlackoutRoomType {
  roomTypeId: string;
  roomTypeName: string;
}

export function getMissingRoomTypesForBlackout(
  blackoutDate: SalesforceBlackoutDate,
  matchingAssignments: Array<RoomRateBlackoutAssignment>,
  roomRateSelections: Array<RoomRateSelection>,
  roomTypeNames: Array<string>,
): Array<MissingBlackoutRoomType> {
  const matchedRoomTypeIds = new Set(matchingAssignments.map((assignment) => assignment.roomTypeId));
  const seenRoomTypeIds = new Set<string>();

  return roomRateSelections
    .map(({ roomType }) => roomType)
    .filter((roomType) => {
      if (seenRoomTypeIds.has(roomType.id) || matchedRoomTypeIds.has(roomType.id)) {
        return false;
      }
      seenRoomTypeIds.add(roomType.id);
      return blackoutAppliesToRoomType(blackoutDate.name, roomType.name, roomTypeNames);
    })
    .map((roomType) => ({ roomTypeId: roomType.id, roomTypeName: roomType.name }));
}

export function groupRoomRateBlackoutAssignments(
  assignments: Array<RoomRateBlackoutAssignment>,
): Array<RoomBlackoutGroup> {
  const rooms = new Map<string, RoomBlackoutGroup>();

  assignments.forEach((assignment) => {
    const room = rooms.get(assignment.roomTypeId) ?? {
      rateGroups: [],
      roomName: assignment.roomName,
      roomTypeId: assignment.roomTypeId,
    };
    const rateGroup = room.rateGroups.find((group) => group.roomRateId === assignment.roomRateId) ?? {
      blackoutDates: [],
      href: assignment.href,
      ratePlanCode: assignment.ratePlanCode,
      ratePlanName: assignment.ratePlanName,
      roomRateId: assignment.roomRateId,
    };

    if (!room.rateGroups.includes(rateGroup)) {
      room.rateGroups.push(rateGroup);
    }

    rateGroup.blackoutDates.push(assignment.roomBlackoutDate);
    rooms.set(assignment.roomTypeId, room);
  });

  return Array.from(rooms.values());
}
